import { cn } from '@/shared/utils';
import { ItemsListSkeleton } from '../ItemsListSkeleton';
import { Card, CardContent, CardFooter } from '@/shared/components';
import { OperationHistoryShowAllButton } from './OperationHistoryShowAllButton';
import {
  useAppSettingsStore,
  useDateLocale,
  useElementHeight,
  useGroupOperationsByDate,
  useMediaQuery,
} from '@/shared/lib';
import { DEFAULT_OPERATIONS_HISTORY_ITEMS_LIMIT } from '@/shared/config';
import { useNavigate } from 'react-router-dom';
import {
  OperationsHistoryItem,
  useOperationHistoryDetailsModal,
  useOperationsRefetch,
} from '@/entities/operation';
import { useTranslation } from 'react-i18next';
import type {
  DictionaryResponse,
  OperationDto,
  OperationsListResponseDto,
  OperationStatusIcon,
  OperationTypeIcon,
} from '@/shared/types';
import { useAccountsRefetch } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import { format } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';

type Props = {
  isBalancesHidden: boolean;
  className?: string;
  isLoading: boolean;
  selectedCardAccountNumbers: string[];
  isAccountCardSelected: boolean;
  operationsHistory?: OperationsListResponseDto;
  operationsStatusIconsData?: DictionaryResponse<OperationStatusIcon[]>;
  operationTypeIconsData?: DictionaryResponse<OperationTypeIcon[]>;
  isOperationsHistoryDataLoading?: boolean;
};

const LastOperationsList = ({
  isBalancesHidden,
  className,
  isLoading: isAccountsRestsLoading,
  selectedCardAccountNumbers,
  isAccountCardSelected,
  operationsHistory,
  operationsStatusIconsData,
  operationTypeIconsData,
  isOperationsHistoryDataLoading,
}: Props) => {
  const { getLanguage } = useAppSettingsStore();
  const { session } = useSessionStore();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { openOperationDetailsModal } = useOperationHistoryDetailsModal();

  const locale = getLanguage() === 'ru' ? ru : enUS;

  const { refetchAll: refetchAccounts } = useAccountsRefetch({
    userId: session?.userId,
  });
  const { refetchAll: refetchOperations } = useOperationsRefetch({
    userId: session?.userId,
  });

  const { t } = useTranslation();
  const { isDateLocaleLoading } = useDateLocale();
  const navigate = useNavigate();

  const { ref: contentRef } = useElementHeight<HTMLDivElement>({
    initialMethod: 'scrollHeight',
    dynamicMethod: 'contentRect',
    trackChanges: true,
  });

  const isLoading =
    isOperationsHistoryDataLoading ||
    isAccountsRestsLoading ||
    isDateLocaleLoading;

  const openHistoryPage = () => navigate('/history');

  const operations = operationsHistory?.operations || [];
  const selected = selectedCardAccountNumbers ?? [];
  const hasSelected = selected.length > 0;

  const filteredOperations = operations.filter(operation => {
    if (!hasSelected) {
      return !isAccountCardSelected;
    }

    return (
      selected.includes(operation.transactionAccount ?? '') ||
      selected.includes(operation.additionalTransactionAccount ?? '')
    );
  });

  const visibleOperations = filteredOperations.slice(
    0,
    DEFAULT_OPERATIONS_HISTORY_ITEMS_LIMIT
  );

  const { groupedOperations } = useGroupOperationsByDate(visibleOperations);

  const shouldShowShowAllButton =
    filteredOperations.length > DEFAULT_OPERATIONS_HISTORY_ITEMS_LIMIT;

  const hasOperations = filteredOperations.length > 0;

  if (isLoading) {
    return <ItemsListSkeleton qty={4} />;
  }

  const isPreferAdditionalAmount = (operation: OperationDto) => {
    return (
      !selected.includes(operation.transactionAccount ?? '') &&
      selected.includes(operation.additionalTransactionAccount ?? '')
    );
  };

  const handleStatusChange = () => {
    refetchAccounts();
    refetchOperations();
  };

  const handleOperationItemClick = (operation: OperationDto) => {
    openOperationDetailsModal({
      operation,
      onStatusChange: handleStatusChange,
    });
  };

  return (
    <Card
      className={cn(
        // By fact x-axis padding is 3.2, but we set half to provide correct hover area
        // Another half is inside of item itself
        'px-[2.4rem] md:px-[1.6rem] py-[2rem] shadow-none flex flex-col tablet:flex-1 tablet:min-h-0 tablet:overflow-hidden',
        className,
        shouldShowShowAllButton && 'pb-0'
      )}>
      <CardContent className="p-0 tablet:overflow-y-auto tablet:min-h-0 tablet:flex-1">
        <div ref={contentRef} className="min-h-0">
          {hasOperations ? (
            <div className={'gap-[2.4rem] flex flex-col'}>
              {Object.entries(groupedOperations).map(
                ([monthYear, operations]) => {
                  const title = format(
                    new Date(`${monthYear}-01`),

                    'LLLL yyyy',
                    {
                      locale,
                    }
                  );

                  return (
                    <div
                      className={'flex flex-col gap-[1.6rem]'}
                      key={monthYear}>
                      <h1
                        className={cn(
                          'headline4 text-primary capitalize',
                          !isMobile ? 'ml-[1.6rem]' : ''
                        )}>
                        {title}
                      </h1>

                      <div className={'flex flex-col'}>
                        {operations.map(operation => (
                          <OperationsHistoryItem
                            isBalancesHidden={isBalancesHidden}
                            key={operation.operaionid}
                            operation={operation}
                            operationStatusIcons={
                              operationsStatusIconsData?.value || []
                            }
                            operationTypeIcons={
                              operationTypeIconsData?.value || []
                            }
                            preferAdditionalAmount={isPreferAdditionalAmount(
                              operation
                            )}
                            onOperationItemClick={handleOperationItemClick}
                          />
                        ))}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          ) : (
            <div className="w-full h-[17.4rem] flex items-center justify-center">
              <div className="w-[22rem] flex flex-col items-center justify-center gap-[0.8rem] text-center">
                <span className="headline4 text-primary">
                  {t('personalAccount.lastOperationsSection.noOperationsTitle')}
                </span>
                <p className="p4 text-secondary">
                  {t(
                    'personalAccount.lastOperationsSection.noOperationsDescription'
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      {shouldShowShowAllButton && (
        <CardFooter className="flex justify-center w-full absolute bottom-0 right-0 bg-gradient-to-b from-background/0 to-background h-[8rem] rounded-ee-[2.4rem] rounded-es-[2.4rem]">
          <OperationHistoryShowAllButton onClick={openHistoryPage} />
        </CardFooter>
      )}
    </Card>
  );
};

export { LastOperationsList };
